import { useLayoutEffect, useRef, useState } from "react";
import { ExternalLink, Trash2 } from "lucide-react";
import { ContextMenuSlotsContext } from "./contextMenuSlots";
import { Button } from "../../../components/ui";

export type ContextMenuProps = {
  children: React.ReactNode;
  onDelete?: () => void;
  onOpen?: () => void;
};

export const ContextMenu = ({
  children,
  onDelete,
  onOpen,
}: ContextMenuProps) => {
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);
  const [optionsSlot, setOptionsSlot] = useState<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const clamp = () => {
      menu.style.transform = "";
      const rect = menu.getBoundingClientRect();
      const margin = 8;
      let shift = 0;
      if (rect.right > window.innerWidth - margin) {
        shift = window.innerWidth - margin - rect.right;
      }
      if (rect.left + shift < margin) {
        shift = margin - rect.left;
      }
      menu.style.transform = shift ? `translateX(${shift}px)` : "";
    };
    clamp();
    window.addEventListener("resize", clamp);
    return () => window.removeEventListener("resize", clamp);
  });

  return (
    <div className="relative">
      <div
        className="bg-hazumu-white text-hazumu-ink scheme-light border border-hazumu-border absolute top-[-32px] right-0 z-40 w-fit rounded-xl flex gap-2 shadow-lg"
        ref={menuRef}
      >
        <div className="contents" ref={setOptionsSlot} />
        <div className="contents" ref={setActionsSlot} />
        {onOpen && (
          <ContextMenuButton icon={ExternalLink} onClick={onOpen}>
            Open
          </ContextMenuButton>
        )}
        <ContextMenuButton
          icon={Trash2}
          color="text-hazumu-danger"
          onClick={onDelete}
        >
          Delete
        </ContextMenuButton>
      </div>
      <ContextMenuSlotsContext.Provider
        value={{ actions: actionsSlot, options: optionsSlot }}
      >
        {children}
      </ContextMenuSlotsContext.Provider>
    </div>
  );
};

export type ContextMenuButtonProps = {
  children: React.ReactNode;
  color?: string;
  icon?: React.ElementType<{ className?: string }>;
  disabled?: boolean;
  onClick?: () => any;
};

export const ContextMenuButton = ({
  children,
  icon,
  onClick,
  disabled = false,
  color,
}: ContextMenuButtonProps) => {
  const Icon = icon;
  return (
    <Button
      variant="ghost"
      size="sm"
      destructive={color === "text-hazumu-danger"}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      disabled={disabled}
    >
      {Icon && <Icon size={14} />}
      {children}
    </Button>
  );
};
